import type { Platform } from '../context/env-context';
import { NavigationPathIdNotResolvedError } from '../types/errors';
import type {
  NavigationOptions,
  Path,
  PathId,
  PathTree,
  VerbosePath,
} from '../types/handlers/navigation';

// Shopify Plus shares Shopify's routes.
const PLATFORM_FALLBACKS: Partial<Record<Platform, Platform>> = {
  shopify_plus: 'shopify',
};

/**
 * Resolve a {@link PathId} to a concrete path and the destination's default {@link NavigationOptions}
 * by walking `tree`.
 *
 * A platform-specific leaf resolves in order: exact `platform`, its alias (e.g. `shopify_plus` ->
 * `shopify`), then `#default`. A matched entry's own opts override the destination's; callers merge
 * their own on top.
 *
 * @throws {NavigationPathIdNotResolvedError} if `id` does not resolve to a leaf, or a platform leaf's
 *   route can't be resolved.
 */
export function resolvePath(
  tree: PathTree<boolean>,
  id: PathId,
  platform?: Platform,
): { path: string; opts?: NavigationOptions } {
  const leaf = id
    .split('.')
    .reduce<unknown>((node, key) => (node as Record<string, unknown> | undefined)?.[key], tree);

  if (typeof leaf === 'string') {
    return { path: leaf };
  }

  // A typed PathTree + PathId guarantees a VerbosePath leaf here; we only guard the runtime case
  // where `tree` does not actually implement `id`.
  if (leaf === null || typeof leaf !== 'object') {
    throw new NavigationPathIdNotResolvedError(id);
  }
  const { path, opts } = leaf as VerbosePath<true>;

  if (typeof path === 'string') {
    return { path, opts };
  }

  const candidates: ('#default' | Platform)[] = [];
  if (platform) {
    candidates.push(platform);
    const alias = PLATFORM_FALLBACKS[platform];
    if (alias) candidates.push(alias);
  }
  candidates.push('#default');

  const entry = candidates
    .map((key) => path[key])
    .find((value): value is Path<false> => value !== undefined);
  if (entry === undefined) {
    throw new NavigationPathIdNotResolvedError(id, platform);
  }

  if (typeof entry === 'string') {
    return { path: entry, opts };
  }
  return { path: entry.path, opts: { ...opts, ...entry.opts } };
}
